import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { ScrollArea } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@creanizer/ui'
import { FileText, Search, Trash2, Copy, ExternalLink, Calendar, User, Dice1, Download } from 'lucide-react'
import { useScenarioStore } from '../../../../stores/scenarioStore'
import { ScenarioDetailDialog } from './ScenarioDetailDialog'
import type { SavedScenario } from '@creanizer/types'
// Toast notifications removed
import { formatDistanceToNow } from 'date-fns'
import { ja } from 'date-fns/locale'

// オブジェクトを安全に文字列化するヘルパー
function toDisplayString(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number') return String(value)
  if (typeof value === 'object') {
    // {text, closingLine} のようなオブジェクトの場合
    if ('text' in value && typeof (value as any).text === 'string') {
      return (value as any).text
    }
    return JSON.stringify(value)
  }
  return String(value)
}

export function SavedScenariosList() {
  const { scenarios, removeScenario } = useScenarioStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [detailScenario, setDetailScenario] = useState<SavedScenario | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [scenarioToDelete, setScenarioToDelete] = useState<string | null>(null)
  
  const filteredScenarios = scenarios.filter(scenario => {
    const query = searchQuery.toLowerCase()
    const title = toDisplayString(scenario.title).toLowerCase()
    const genre = toDisplayString(scenario.result?.basicInfo?.genre).toLowerCase()
    const system = toDisplayString(scenario.result?.basicInfo?.system).toLowerCase()
    const summary = toDisplayString(scenario.result?.basicInfo?.summary).toLowerCase()
    return (
      title.includes(query) ||
      genre.includes(query) ||
      system.includes(query) ||
      summary.includes(query)
    )
  })
  
  const handleCopy = (scenario: typeof scenarios[0]) => {
    const jsonData = JSON.stringify(scenario.result, null, 2)
    navigator.clipboard.writeText(jsonData)
    console.log('シナリオをコピーしました')
  }
  
  const handleView = (scenario: typeof scenarios[0]) => {
    setDetailScenario(scenario)
  }

  const handleDownloadText = (scenario: typeof scenarios[0]) => {
    const text = formatScenarioText(scenario)
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const title = scenario.result?.title || scenario.result?.basicInfo?.title || scenario.title || 'scenario'
    a.download = `${toDisplayString(title)}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  // 再帰的にオブジェクトをテキスト化
  const formatValue = (value: any, indent: number = 0): string => {
    if (value === null || value === undefined) return ''
    const prefix = '  '.repeat(indent)
    if (typeof value === 'string') return value
    if (typeof value === 'number' || typeof value === 'boolean') return String(value)
    if (Array.isArray(value)) {
      return value.map((item, i) => {
        if (typeof item === 'string' || typeof item === 'number') return `${prefix}- ${item}`
        const itemTitle = item.title || item.name || `項目${i + 1}`
        return `${prefix}■ ${itemTitle}\n${formatValue(item, indent + 1)}`
      }).join('\n')
    }
    if (typeof value === 'object') {
      if ('text' in value && typeof value.text === 'string') return value.text
      return Object.entries(value)
        .filter(([_, v]) => v !== null && v !== undefined && v !== '')
        .map(([key, val]) => {
          if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
            return `${prefix}${key}: ${val}`
          }
          if (Array.isArray(val) && val.length === 0) return ''
          return `${prefix}【${key}】\n${formatValue(val, indent + 1)}`
        })
        .filter(Boolean)
        .join('\n')
    }
    return String(value)
  }

  // シナリオ全体をテキストフォーマット
  const formatScenarioText = (scenario: typeof scenarios[0]): string => {
    const r = scenario.result
    if (!r) return 'シナリオデータがありません'
    const title = r.title || r.basicInfo?.title || scenario.title || 'タイトルなし'
    let text = `${'='.repeat(50)}\n${toDisplayString(title)}\n${'='.repeat(50)}\n\n`
    Object.entries(r).forEach(([key, value]) => {
      if (value === null || value === undefined || key === 'metadata') return
      if (Array.isArray(value) && value.length === 0) return
      if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return
      text += `${'─'.repeat(40)}\n【${key}】\n${'─'.repeat(40)}\n`
      text += formatValue(value, 0) + '\n\n'
    })
    return text
  }
  
  const handleDelete = (id: string) => {
    setScenarioToDelete(id)
    setDeleteDialogOpen(true)
  }
  
  const confirmDelete = () => {
    if (scenarioToDelete) {
      removeScenario(scenarioToDelete)
      console.log('シナリオを削除しました')
      setScenarioToDelete(null)
    }
    setDeleteDialogOpen(false)
  }
  
  if (scenarios.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500">保存されたシナリオはありません</p>
          <p className="text-sm text-gray-400 mt-2">
            生成したシナリオを保存すると、ここに表示されます
          </p>
        </CardContent>
      </Card>
    )
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="シナリオを検索..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Badge variant="secondary">
          {filteredScenarios.length} / {scenarios.length} 件
        </Badge>
      </div>
      
      <ScrollArea className="h-[600px]">
        <div className="space-y-3 pr-4">
          {filteredScenarios.map((scenario) => (
            <Card key={scenario.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1">
                    <CardTitle className="text-lg">
                      {toDisplayString(scenario.title) || 'タイトルなし'}
                    </CardTitle>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {scenario.result?.basicInfo?.genre && (
                        <Badge variant="outline">
                          {toDisplayString(scenario.result.basicInfo.genre)}
                        </Badge>
                      )}
                      {scenario.result?.basicInfo?.system && (
                        <Badge variant="outline">
                          <Dice1 className="w-3 h-3 mr-1" />
                          {toDisplayString(scenario.result.basicInfo.system)}
                        </Badge>
                      )}
                      {scenario.result?.basicInfo?.playerCount && (
                        <Badge variant="outline">
                          <User className="w-3 h-3 mr-1" />
                          {toDisplayString(scenario.result.basicInfo.playerCount)}
                        </Badge>
                      )}
                    </div>
                  </div>
                  {/* デスクトップ版: 縦並びボタン */}
                  <div className="hidden sm:flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleView(scenario)}
                      title="詳細を表示"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownloadText(scenario)}
                      title="テキストでダウンロード"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopy(scenario)}
                      title="JSONをコピー"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(scenario.id)}
                      title="削除"
                      className="text-red-500 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {scenario.result?.basicInfo?.summary && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">
                    {toDisplayString(scenario.result.basicInfo.summary)}
                  </p>
                )}
                
                {/* モバイル版: 横一列のボタン */}
                <div className="flex sm:hidden gap-1 mb-3 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleView(scenario)}
                    title="詳細"
                    className="flex-1"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span className="ml-1">詳細</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadText(scenario)}
                    title="DL"
                    className="flex-1"
                  >
                    <Download className="w-4 h-4" />
                    <span className="ml-1">DL</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(scenario.id)}
                    title="削除"
                    className="flex-1 text-red-500 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="ml-1">削除</span>
                  </Button>
                </div>
                
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDistanceToNow(new Date(scenario.createdAt), { 
                      addSuffix: true,
                      locale: ja 
                    })}
                  </div>
                  <div className="flex gap-3">
                    {scenario.result?.scenes && (
                      <div>
                        {scenario.result.scenes.length}シーン
                      </div>
                    )}
                    {scenario.result?.npcs && (
                      <div>
                        NPC {scenario.result.npcs.length}人
                      </div>
                    )}
                    {scenario.result?.items && (
                      <div>
                        アイテム {scenario.result.items.length}個
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </ScrollArea>
      
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>シナリオを削除しますか？</DialogTitle>
            <DialogDescription>
              この操作は取り消せません。シナリオは完全に削除されます。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
            <Button onClick={confirmDelete} className="bg-red-500 hover:bg-red-600">
              削除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ScenarioDetailDialog
        scenario={detailScenario}
        open={!!detailScenario}
        onClose={() => setDetailScenario(null)}
      />
    </div>
  )
}